<template>
	<TabsComponent
		:tabs="props.tabs"
		:default-tab-index="props.defaultTabIndex"
		:set-border="props.setBorder"
		:pass-selection-to-route="props.passSelectionToRoute"
		:keep-alive="props.keepAlive"
		class="vcx-tabs-component"
		@active-tab="emit('active-tab', $event)"
	>
		<template #tabs-header>
			<slot name="tabs-header"></slot>
		</template>
	</TabsComponent>
</template>

<script setup lang="ts">
import { Tab } from 'types/layout';
import TabsComponent from '@components/layout/tabs-component.vue';

interface Props {
	tabs: Array<Tab>;
	defaultTabIndex?: number;
	setBorder?: boolean;
	passSelectionToRoute?: boolean;
	keepAlive?: boolean;
}

interface Emits {
	(e: 'active-tab', value: Tab): void;
}

const props = withDefaults(defineProps<Props>(), {
	defaultTabIndex: 0,
	setBorder: false,
	passSelectionToRoute: false,
	keepAlive: false
});

const emit = defineEmits<Emits>();
</script>

<style lang="scss" scoped>
.vcx-tabs-component {
	:deep(.tab-list) {
		color: var(--vcx-gray-40);
		display: flex;
		font-family: var(--vcx-font-body);
		flex-direction: row;
		justify-content: left;
		align-items: flex-start;
		gap: 2rem;
	}

	:deep(.tab) {
		background: transparent;
		border: none;
		border-radius: 0;
		font-size: 18px;
		font-weight: 600;
		line-height: 1.5;
		padding: 0 4px 8px;
		position: relative;

		&::before {
			background: var(--vcx-gray-white);
			bottom: 0;
			content: '';
			height: 2px;
			left: 0;
			opacity: 0;
			position: absolute;
			transition: all 300ms ease;
			width: 100%;
		}

		&:hover {
			color: var(--vcx-gray-white);

			&::before {
				opacity: 1;
			}
		}

		&:focus-visible {
			color: var(--vcx-gray-white);
			outline: -webkit-focus-ring-color auto 1px;
		}
	}

	:deep(.tab-active) {
		color: var(--vcx-gray-white);

		&::before {
			opacity: 1;
		}
	}
}
</style>
